import { Component, Input, ViewEncapsulation } from '@angular/core';
import { ISendMailModel, SendMailComponent } from '../send-mail/send-mail.component';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../formly/ga-formly.service';
import { FormattingService } from '../services/formatting.service';
import { GaValidateService } from '../formly/ga-validate.service';
import { TranslationService } from '../translation/translation.service';
import { Override } from '../app-decorators';
import { testEmailAddressFn } from '../formly/ga-formly.util';
import { EDITOR_TINYMCE } from '../editor/editor-config.service';
import { DefaultFormDialogComponent } from '../message/dialogs/default-form-dialog.component';
import * as _ from 'underscore';
import { CancelIdContact, ContactDataService } from '../contact/contact-data.service';

export interface IMailInvoiceModel extends ISendMailModel {
  sendToInvoiceContact?:boolean;
}

@Component({
  selector: 'mail-invoice',
  templateUrl: '../message/dialogs/default-form-dialog.html',
  styleUrls: ['../send-mail/send-mail.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MailInvoiceComponent extends SendMailComponent {
  private sendInvoiceContactCheckField: IExtraFieldConfigurationObject;
  private contactGroupField: IExtraFieldConfigurationObject;

  @Input() public hideSubject:boolean;
  @Input() public checkboxLabel:string;

  constructor(
    formlyService: GaFormlyService,
    contactDataService: ContactDataService,
    formattingService: FormattingService,
    validateService: GaValidateService,
    translationService: TranslationService
  ) {
    super(formlyService, contactDataService, formattingService, validateService, translationService);
  }

  @Override(SendMailComponent)
  public createFields(): IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-md-1';
    const valueClass:string = 'col-md-11';
    const label:string = this.checkboxLabel || 'Send invoice (if not activated, proforma will be sent)';


    return [
      this.contactGroupField = this.formlyService.aGroup('contactGroup', 'To')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFields([
          this.toField = this.formlyService.aMultiSelect('to', 'To')
            .withoutWrapper()
            .withValueClass('col-md-12')
            .withFormat(this.formattingService.formatContactWithCompany)
            .withExtraOptions({
              templateOptions: {
                searchFunction: {
                  request: (filter:string) => this.contactDataService.getContacts(filter, CancelIdContact.TO),
                  cancel: () => {
                    return this.contactDataService.cancelGetContacts(CancelIdContact.TO);
                  }
                },
                customValueTestFn: testEmailAddressFn,
                hideValidationMessage: true
              }
            })
            .onChange(() => this.contactGroupField.formControl?.updateValueAndValidity())
            .build(),

          this.sendInvoiceContactCheckField = this.formlyService
            .aCheckbox('sendToInvoiceContact', label)
            .withoutWrapper()
            .withValueClass('col-md-12')
            .onChange(() => this.contactGroupField.formControl?.updateValueAndValidity())
            .build()
        ])
        .withExtraOptions({
          validators: {
            required: {
              expression: (): boolean => {
                return (!_.isUndefined(this.model.sendToInvoiceContact) && this.model.sendToInvoiceContact) || !!this.model.to?.length;
              },
              message: 'You have to fill in a contact or choose the option '.concat(`'${label}'`)
            }
          }
        })
        .build(),

      this.ccField = this.formlyService.aMultiSelect('cc', 'CC')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatContactWithCompany)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => this.contactDataService.getContacts(filter, CancelIdContact.CC),
              cancel: () => {
                return this.contactDataService.cancelGetContacts(CancelIdContact.CC);
              }
            },
            customValueTestFn: testEmailAddressFn
          }
        })
        .build(),

      this.bccField = this.formlyService.aMultiSelect('bcc', 'BCC')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatContactWithCompany)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => this.contactDataService.getContacts(filter, CancelIdContact.BCC),
              cancel: () => {
                return this.contactDataService.cancelGetContacts(CancelIdContact.BCC);
              }
            },
            customValueTestFn: testEmailAddressFn
          }
        })
        .build(),

      this.subjectField = this.formlyService.anInput('subject', 'Subject')
        .isNotRequired()
        .withHideExpression(() => this.hideSubject)
        .withWrapperClass('subject-field')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.bodyField = this.formlyService.aTextarea('body', 'Body')
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withExtraOptions({
          templateOptions: {
            editor: EDITOR_TINYMCE
          }
        })
        .build()
    ];
  }

  @Override(SendMailComponent)
  public setFormFieldDisabled(disabled:boolean) {
    this.disabled = disabled;
    this.buttons[0].disabled = disabled;
    this.toField.templateOptions.disabled = disabled;
    this.ccField.templateOptions.disabled = disabled;
    this.bccField.templateOptions.disabled = disabled;
    this.sendInvoiceContactCheckField.templateOptions.disabled = disabled;
    this.bodyField.templateOptions.disabled = disabled;
  }

  // @ts-ignore
  @Override(DefaultFormDialogComponent)
  public save = (): void => {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        // update validators because group field is not included in formControl
        if (this.contactGroupField.validators.required.expression()) {
          this.onSave.emit(this.getModel());
        }
      })
      .catch((e) => {
        console.debug('Mail invoice error', e);
      });
  };
}